import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Heart, 
  Menu, 
  X, 
  User, 
  LogOut, 
  LayoutDashboard,
  Store,
  Crown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { href: '/vendors', label: 'Vendors' },
    { href: '/inspiration', label: 'Inspiration' },
    { href: '/planning', label: 'Planning' },
    { href: '/blog', label: 'Blog' },
  ];

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  const getNavLinkClass = (path) => {
    if (isActive(path)) {
      return isHomePage ? 'bg-gold-500/20 text-gold-300' : 'bg-maroon-50 text-maroon-700';
    }
    return isHomePage ? 'text-cream-200 hover:text-white hover:bg-white/10' : 'text-maroon-600 hover:text-maroon-900 hover:bg-cream-100';
  };

  const getMobileNavLinkClass = (path) => {
    if (isActive(path)) {
      return isHomePage ? 'bg-gold-500/20 text-gold-300' : 'bg-maroon-50 text-maroon-700';
    }
    return isHomePage ? 'text-cream-200 hover:bg-white/10' : 'text-maroon-600 hover:bg-cream-50';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className={`sticky top-0 z-50 transition-all ${
      isHomePage 
        ? 'bg-maroon-900/95 backdrop-blur-sm border-b border-gold-500/20' 
        : 'bg-white/95 backdrop-blur-sm border-b border-cream-200'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-gold">
              <Crown className="w-5 h-5 text-maroon-900" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${getNavLinkClass(link.href)}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/become-vendor">
              <Button 
                variant="ghost" 
                size="sm" 
                className={`rounded-lg ${
                  isHomePage 
                    ? 'text-gold-300 hover:text-gold-200 hover:bg-white/10' 
                    : 'text-maroon-600 hover:text-maroon-900'
                }`}
              >
                <Store className="w-4 h-4 mr-1.5" />
                For Vendors
              </Button>
            </Link>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className={`rounded-lg gap-2 ${isHomePage ? 'hover:bg-white/10' : ''}`}
                    data-testid="user-menu-trigger"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full border-2 border-gold-400" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-gold-600" />
                      </div>
                    )}
                    <span className={`font-medium ${isHomePage ? 'text-white' : 'text-maroon-700'}`}>
                      {user?.name?.split(' ')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-xl">
                  <DropdownMenuItem asChild className="rounded-lg cursor-pointer">
                    <Link to="/dashboard" className="flex items-center gap-2">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="rounded-lg cursor-pointer text-maroon-600 focus:text-maroon-700"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button 
                  className={isHomePage ? 'btn-gold h-10 px-5' : 'btn-primary h-10 px-5'} 
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg ${
              isHomePage ? 'hover:bg-white/10 text-white' : 'hover:bg-cream-100 text-maroon-900'
            }`}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden ${isHomePage ? 'bg-maroon-900' : 'bg-white'} border-t ${
              isHomePage ? 'border-gold-500/20' : 'border-cream-200'
            }`}
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl text-base font-medium ${getMobileNavLinkClass(link.href)}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/become-vendor"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-base font-medium ${
                  isHomePage ? 'text-gold-300 hover:bg-white/10' : 'text-maroon-600 hover:bg-cream-50'
                }`}
              >
                For Vendors
              </Link>
              <div className={`pt-4 border-t ${isHomePage ? 'border-gold-500/20' : 'border-cream-200'}`}>
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block px-4 py-3 rounded-xl font-medium ${
                        isHomePage ? 'bg-gold-500/20 text-gold-300' : 'bg-maroon-50 text-maroon-700'
                      }`}
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 rounded-xl text-maroon-500 font-medium mt-2"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-xl text-center font-medium ${
                      isHomePage 
                        ? 'bg-gradient-to-r from-gold-400 to-gold-600 text-maroon-900' 
                        : 'bg-gradient-to-r from-maroon-600 to-maroon-700 text-white'
                    }`}
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
